import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
  CommissionStatus,
} from '../../constants/partnerNetwork';
import {
  AdminCommission,
  listCommissions,
  markCommissionPaid,
} from '../../services/partnerNetwork';
import { formatDate, formatMoney, formatRate, toDateInput } from '../../utils/partnerFormat';
import {
  Alert,
  EmptyState,
  Modal,
  Pagination,
  Spinner,
  StatCard,
  StatusBadge,
  filterClass,
  inputClass,
} from './ui';

const STATUSES: CommissionStatus[] = ['pending', 'due', 'paid', 'cancelled'];

/** Where commissions are approved, chased and marked paid. */
const CommissionList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [commissions, setCommissions] = useState<AdminCommission[]>([]);
  const [totals, setTotals] = useState<
    Record<CommissionStatus, { amount: number; count: number }> | null
  >(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [paying, setPaying] = useState<AdminCommission | null>(null);
  const [reference, setReference] = useState('');
  const [paidDate, setPaidDate] = useState(toDateInput(new Date()));

  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || 1);

  const setFilter = (key: string, value: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listCommissions({
        status: status || undefined,
        page,
        limit: 25,
      });
      setCommissions(response.data);
      setTotals(response.totals);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load commissions');
    } finally {
      setLoading(false);
    }
  }, [status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const savePayment = async () => {
    if (!paying) return;

    setError(null);
    if (!reference.trim()) {
      setError('A payment reference is required.');
      return;
    }

    try {
      setBusy(true);
      await markCommissionPaid(paying.id, {
        paymentReference: reference.trim(),
        paidDate: paidDate || undefined,
      });
      setNotice(
        `${formatMoney(paying.commissionAmount, paying.currency)} recorded as paid${
          paying.partner ? ` to ${paying.partner.fullName}` : ''
        }.`
      );
      setPaying(null);
      setReference('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the payment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Commissions
            </h1>
            <p className="text-sm text-muted">
              What partners have earned, what is owed, and what has been paid.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}
        {notice && !error && (
          <Alert tone="success" className="mb-6">
            {notice}
          </Alert>
        )}

        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {STATUSES.map((value) => (
            <StatCard
              key={value}
              label={COMMISSION_STATUS_LABELS[value]}
              value={formatMoney(totals?.[value]?.amount ?? 0)}
              hint={`${totals?.[value]?.count ?? 0} ${
                (totals?.[value]?.count ?? 0) === 1 ? 'record' : 'records'
              }`}
              tone={value === 'due' ? 'warning' : value === 'paid' ? 'success' : 'default'}
              active={status === value}
              onClick={() => setFilter('status', status === value ? null : value)}
            />
          ))}
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-card p-4">
          <select
            value={status}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {COMMISSION_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
          {status && (
            <button
              type="button"
              onClick={() => setFilter('status', null)}
              className="text-sm text-muted underline underline-offset-2 hover:text-white"
            >
              Clear
            </button>
          )}
        </div>

        <div className="rounded-xl border border-surface-border bg-surface-card">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner className="h-10 w-10" />
            </div>
          ) : commissions.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No commissions yet"
                description="A commission is created when a referral is given a project value. Set one on a won referral to start."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 font-medium">Partner</th>
                    <th className="px-4 py-3 font-medium">Referral</th>
                    <th className="px-4 py-3 font-medium">Project value</th>
                    <th className="px-4 py-3 font-medium">Rate</th>
                    <th className="px-4 py-3 font-medium">Commission</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Paid</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((commission) => (
                    <tr
                      key={commission.id}
                      className="border-b border-surface-border/60 transition-colors last:border-0 hover:bg-surface-hover/40"
                    >
                      <td className="px-4 py-3">
                        {commission.partner ? (
                          <Link
                            to={`/admin/partners/partners/${commission.partner.id}`}
                            className="font-medium text-white hover:text-primary-400"
                          >
                            {commission.partner.fullName}
                            <span className="block font-mono text-xs text-muted">
                              {commission.partner.partnerId}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {commission.referral ? (
                          <Link
                            to={`/admin/partners/referrals/${commission.referral.id}`}
                            className="text-muted hover:text-white"
                          >
                            <span className="block font-mono text-xs text-primary-400">
                              {commission.referral.referralId}
                            </span>
                            {commission.referral.businessName}
                          </Link>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatMoney(commission.eligibleAmount, commission.currency)}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatRate(commission.commissionRate)}
                      </td>
                      <td className="px-4 py-3 font-medium text-white">
                        {formatMoney(commission.commissionAmount, commission.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={COMMISSION_STATUS_LABELS[commission.status]}
                          tone={COMMISSION_STATUS_TONES[commission.status]}
                        />
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {commission.paidDate ? (
                          <>
                            {formatDate(commission.paidDate)}
                            <span className="block max-w-[140px] truncate text-xs text-muted/70">
                              {commission.paymentReference}
                            </span>
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {commission.status === 'pending' || commission.status === 'due' ? (
                          <button
                            type="button"
                            onClick={() => setPaying(commission)}
                            className="rounded-lg border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs text-primary-400 transition-colors hover:bg-primary-500/20"
                          >
                            Mark paid
                          </button>
                        ) : (
                          <span className="text-xs text-muted">
                            {commission.paidByName || '—'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && commissions.length > 0 && (
            <div className="border-t border-surface-border px-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                onChange={(next) => setFilter('page', String(next))}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={Boolean(paying)}
        title="Record payment"
        description="This is the audit record of the payment, so it cannot be edited afterwards."
        onClose={() => {
          setPaying(null);
          setReference('');
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setPaying(null);
                setReference('');
              }}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={savePayment}
              disabled={busy || !reference.trim()}
              className="btn-primary py-2.5 px-5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? 'Recording…' : 'Mark as paid'}
            </button>
          </>
        }
      >
        {paying && (
          <div className="space-y-4">
            <div className="rounded-lg border border-surface-border bg-surface px-4 py-3 text-sm">
              <span className="text-muted">Paying </span>
              <span className="font-medium text-white">
                {formatMoney(paying.commissionAmount, paying.currency)}
              </span>
              {paying.partner && (
                <span className="text-muted"> to {paying.partner.fullName}</span>
              )}
              {paying.referral && (
                <span className="block text-xs text-muted">
                  For {paying.referral.referralId} — {paying.referral.businessName}
                </span>
              )}
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Payment reference *</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Bank transfer reference"
                className={inputClass}
                autoFocus
              />
            </div>
            <div>
              <label className="mb-2 block text-sm text-muted">Paid on</label>
              <input
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default CommissionList;
